import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Navigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useLanguage } from '../contexts/LanguageContext';
import { useAccess } from '../contexts/AccessContext';
import ToolPageHeader from '../components/ToolPageHeader';
import DateNavigator from '../components/DateNavigator';
import axios from 'axios';
import { getBackendUrl } from '../utils/api';
import storage from '../utils/storage';
import { SECTIONS_CONFIG } from '../config/sectionsConfig';
import { 
  Sparkles, 
  Newspaper, 
  Gamepad2, 
  Music,
  ChevronDown,
  ChevronRight,
  Radio,
  Clock,
  RefreshCw,
  Loader2,
  Shield,
  Sun,
  Download,
  FileText,
  Wand2,
  Plus
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import AIContentEditorModal from '../components/AIContentEditorModal';
import RadioClockBuilder from '../components/RadioClockBuilder';
import { Clock as ClockIcon } from 'lucide-react';

// Helper function to clean content - same as App.js
const cleanContent = (text) => {
  if (!text) return text;
  let cleaned = text;
  // Remove standalone "i" characters that appear before content info lines
  cleaned = cleaned.replace(/^i\s*$/gm, '');
  cleaned = cleaned.replace(/\ni\s*\n/g, '\n');
  cleaned = cleaned.replace(/_Περιεχόμενο από: \d{4}-\d{2}-\d{2}_/g, '');
  cleaned = cleaned.replace(/\*Περιεχόμενο από \d{4}-\d{2}-\d{2}\*/g, '');
  cleaned = cleaned.replace(/i\s*\*Περιεχόμενο από \d{4}-\d{2}-\d{2}\*/g, '');
  cleaned = cleaned.replace(/Περιεχόμενο από \d{4}-\d{2}-\d{2}/g, '');
  cleaned = cleaned.replace(/Προσωρινό Περιεχόμενο[^.]*\./g, '');
  cleaned = cleaned.replace(/Χρησιμοποιούμε περιεχόμενο από[^.]*\./g, '');
  // Remove lines that are just "i" followed by date info patterns
  cleaned = cleaned.replace(/^i\s+\*?Περιεχόμενο.*$/gm, '');
  cleaned = cleaned.replace(/\n{3,}/g, '\n\n');
  return cleaned.trim();
};

// Helper function to render basic markdown as HTML
const renderMarkdown = (text) => {
  if (!text) return { __html: '' };
  
  let html = text;
  
  // Escape HTML characters first (for security)
  html = html.replace(/&/g, '&amp;');
  html = html.replace(/</g, '&lt;');
  html = html.replace(/>/g, '&gt;');
  
  // Bold: **text** or __text__
  html = html.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
  html = html.replace(/__([^_]+)__/g, '<strong>$1</strong>');
  
  // Italic: *text* or _text_
  html = html.replace(/\*([^*]+)\*/g, '<em>$1</em>');
  html = html.replace(/_([^_]+)_/g, '<em>$1</em>');
  
  // Newlines to <br>
  html = html.replace(/\n/g, '<br>');
  
  return { __html: html };
};

// Safe Image component - same as App.js
const SafeImage = ({ src, alt, className }) => {
  const [hasError, setHasError] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  
  if (!src || src.trim() === '' || src === 'null' || src === 'undefined' || hasError) {
    return null;
  }
  
  return (
    <img 
      src={src} 
      alt={alt || ''} 
      className={`${className} ${isLoaded ? '' : 'opacity-0'}`}
      onError={() => setHasError(true)}
      onLoad={() => setIsLoaded(true)}
      loading="lazy"
      style={{ display: hasError ? 'none' : 'block' }}
    />
  );
};

// Placeholder indicators to filter out
const PLACEHOLDER_INDICATORS = [
  'Ενημερώνεται καθημερινά',
  'Επιστρέψτε σύντομα',
  'ΣΥΝΕΧΗΣ ΕΝΗΜΕΡΩΣΗ',
  'Προσωρινό Περιεχόμενο',
  'τεχνικού προβλήματος',
  'Ευχαριστούμε για την υπομονή',
  '_Πηγή:',
  'Προσωρινά μη διαθέσιμο',
  '⚠️',
];

const OnAirContentPage = ({ isAuthenticated, currentUser }) => {
  const { t, currentLang: language } = useLanguage();
  const { access, hasFullAccess, hasFeatureAccess } = useAccess();
  const [content, setContent] = useState(null);
  const [translations, setTranslations] = useState(null);
  const [translationStatus, setTranslationStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedCategories, setExpandedCategories] = useState({});
  const [animationStep, setAnimationStep] = useState(0);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [aiEditor, setAiEditor] = useState({ open: false, mode: 'edit', sectionKey: '', sectionTitle: '', currentText: '' });
  const [clockBuilderOpen, setClockBuilderOpen] = useState(false);
  
  const API = getBackendUrl();

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Staggered animation
  useEffect(() => {
    const timers = [];
    for (let i = 1; i <= 4; i++) {
      timers.push(setTimeout(() => setAnimationStep(i), i * 150));
    }
    return () => timers.forEach(t => clearTimeout(t));
  }, []);

  // Fetch content - same API as main page
  useEffect(() => {
    const fetchContent = async () => {
      try {
        setLoading(true);
        const token = storage.getItem('token');
        const headers = {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        };
        
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }
        
        const cacheBuster = `?_t=${Date.now()}`;
        const response = await axios.get(`${API}/api/content/latest${cacheBuster}`, { headers });
        setContent(response.data);
      } catch (e) {
        console.error('Error fetching content:', e);
      } finally {
        setLoading(false);
      }
    };
    
    if (isAuthenticated) {
      fetchContent();
    }
  }, [API, isAuthenticated]);

  // Fetch translations when language changes (non-Greek)
  useEffect(() => {
    const fetchTranslations = async () => {
      if (language === 'el') {
        setTranslations(null);
        setTranslationStatus(null);
        return;
      }
      
      try {
        // First check translation status
        const statusResponse = await axios.get(`${API}/api/translations/status`);
        setTranslationStatus(statusResponse.data);
        
        const langStatus = statusResponse.data?.languages?.[language];
        if (langStatus?.status === 'available') {
          // Fetch translated content
          const transResponse = await axios.get(`${API}/api/translations/content/${language}`);
          if (transResponse.data?.status === 'available') {
            setTranslations(transResponse.data.content);
          }
        }
      } catch (e) {
        console.error('Error fetching translations:', e);
      }
    };
    
    if (content && language !== 'el') {
      fetchTranslations();
    }
  }, [API, content, language]);

  // Redirect if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/trial-signup" replace />;
  }

  // Check access
  const hasAccess = hasFullAccess() || 
    access?.access_type === 'trial' || 
    access?.access_type === 'admin' || 
    access?.access_type === 'subscriber' ||
    access?.access_type === 'free_access' ||
    currentUser?.role === 'admin' || 
    currentUser?.role === 'trial';

  // Category configuration with translations
  const categories = [
    {
      id: 'morning',
      icon: Sun,
      title: language === 'el' ? '☀️ ΠΡΩΙΝΟ (06:00-10:00)' : '☀️ MORNING (06:00-10:00)',
      description: language === 'el' 
        ? 'Περιεχόμενο για νυσταγμένους ακροατές: Ανθρώπινες στιγμές, Διλήμματα, Εξομολογήσεις, Κοινές ενοχλήσεις'
        : 'Content for sleepy listeners: Human moments, Dilemmas, Confessions, Shared annoyances',
      sections: ['morning'],
      color: 'from-yellow-400 to-orange-500',
      bgColor: 'bg-yellow-500/10'
    },
    {
      id: 'daytime',
      icon: Sun,
      title: language === 'el' ? '🌤️ DAYTIME (10:00-16:00)' : '🌤️ DAYTIME (10:00-16:00)',
      description: language === 'el' 
        ? 'Για ακροατές που δουλεύουν: Μικρές αποδράσεις, Office life, Fun facts, Light debates'
        : 'For working listeners: Small escapes, Office life, Fun facts, Light debates',
      sections: ['daytime'],
      color: 'from-cyan-400 to-blue-500',
      bgColor: 'bg-cyan-500/10'
    },
    {
      id: 'afternoon',
      icon: Sun,
      title: language === 'el' ? '🌅 ΑΠΟΓΕΥΜΑ / DRIVE TIME (16:00-19:00)' : '🌅 AFTERNOON / DRIVE TIME (16:00-19:00)',
      description: language === 'el' 
        ? 'Περιεχόμενο για κουρασμένους ακροατές: Μετάβαση δουλειά→σπίτι, Εκτόνωση, Κίνηση'
        : 'Content for tired commuters: Work→Home transition, Venting, Traffic talk',
      sections: ['afternoon'],
      color: 'from-orange-500 to-red-500',
      bgColor: 'bg-orange-500/10'
    },
    {
      id: 'evening',
      icon: Music,
      title: language === 'el' ? '🌙 ΒΡΑΔΥ (19:00-24:00)' : '🌙 EVENING (19:00-24:00)',
      description: language === 'el' 
        ? 'Μουσικό περιεχόμενο: Κρυφά διαμάντια, Ιστορίες καλλιτεχνών, Acoustic versions, Νοσταλγία'
        : 'Musical content: Hidden gems, Artist stories, Acoustic versions, Nostalgia',
      sections: ['evening'],
      color: 'from-indigo-500 to-purple-600',
      bgColor: 'bg-indigo-500/10'
    },
    {
      id: 'weekend',
      icon: Sparkles,
      title: language === 'el' ? '🎉 ΣΑΒΒΑΤΟΚΥΡΙΑΚΟ' : '🎉 WEEKEND',
      description: language === 'el' 
        ? 'Ιδέες για έξοδο, Οδηγός πόλης, Νέες κυκλοφορίες, Events, Sunday Chill'
        : 'Date ideas, City guide, New releases, Events, Sunday Chill',
      sections: ['weekend'],
      color: 'from-pink-500 to-rose-500',
      bgColor: 'bg-pink-500/10'
    },
    {
      id: 'for-women-only',
      icon: Sparkles,
      title: language === 'el' ? '💖 Για γυναίκες μόνο' : '💖 For Women Only',
      description: language === 'el'
        ? 'Ημερήσιο περιεχόμενο για γυναίκες 25–44 (Hot AC): 4 θέματα συζήτησης + 1 διαγωνισμός'
        : 'Daily content for women 25–44 (Hot AC): 4 talk topics + 1 contest',
      sections: ['for_women_only'],
      color: 'from-fuchsia-500 to-pink-500',
      bgColor: 'bg-fuchsia-500/10'
    },
    {
      id: 'zodiac-lifestyle',
      icon: Sparkles,
      title: t('zodiacLifestyle') || (language === 'el' ? 'Ζώδια & Lifestyle' : 'Zodiac & Lifestyle'),
      description: t('zodiacLifestyleDesc') || (language === 'el' 
        ? 'Ζώδια της ημέρας, Sex & Relationships, Υγεία & Ευεξία, Οικονομικά tips'
        : 'Daily horoscope, Sex & Relationships, Health & Wellness, Financial tips'),
      sections: ['zodiac', 'sex_relationships', 'health_wellness', 'personal_finance'],
      color: 'from-purple-500 to-violet-600',
      bgColor: 'bg-purple-500/10'
    },
    {
      id: 'news-trends',
      icon: Newspaper,
      title: t('newsTrends') || (language === 'el' ? 'Ειδήσεις & Trends' : 'News & Trends'),
      description: t('newsTrendsDesc') || (language === 'el'
        ? 'Weird News, Good News, Sports News, Daily Trends, Καιρός, Research Says, Birthdays'
        : 'Weird News, Good News, Sports News, Daily Trends, Weather, Research Says, Birthdays'),
      sections: ['weird_news', 'good_news', 'sports_news', 'trends', 'weather', 'research_says', 'world_days', 'bad_recipes', 'strange_weird', 'daily_record', 'birthdays', 'music_lifestyle'],
      color: 'from-blue-500 to-cyan-500',
      bgColor: 'bg-blue-500/10'
    },
    {
      id: 'entertainment-games',
      icon: Gamepad2,
      title: t('entertainmentGames') || (language === 'el' ? 'Διασκέδαση & Παιχνίδια' : 'Entertainment & Games'),
      description: t('entertainmentGamesDesc') || (language === 'el'
        ? 'Ραδιοφωνικά contests, Παιχνίδια λέξεων, Debates, Αλήθεια ή Μύθος'
        : 'Radio contests, Word games, Debates, Truth or Myth'),
      sections: [
        'radio_contest', 'one_word_game', 'truth_or_myth', 'would_you_rather', 
        'debate_of_the_day', 'games_riddles', 'fastest_wins_contest', 'yes_or_no',
        'male_vs_female', 'man_says_woman_responds', 'todays_confession',
        'real_or_epic_lie', 'what_drives_me_crazy', 'guess_the_profession',
        'guess_song_from_lyric', 'greek_habits_never_die', 'funny_habits',
        'tech_innovation', 'small_secrets'
      ],
      color: 'from-orange-500 to-amber-500',
      bgColor: 'bg-orange-500/10'
    },
    {
      id: 'music-charts',
      icon: Music,
      title: t('musicCharts') || (language === 'el' ? 'Μουσική & Charts' : 'Music & Charts'),
      description: t('musicChartsDesc') || (language === 'el'
        ? 'Apple Music Greece, YouTube Trending, Σαν Σήμερα στη Μουσική'
        : 'Apple Music Greece, YouTube Trending, Today in Music History'),
      sections: ['shazam_chart_greece', 'youtube_trending_greece', 'today_in_music_history', 'on_this_day_charts', 'radio_top_songs'],
      // spotify_chart_greece ΑΦΑΙΡΕΘΗΚΕ - scraper not working
      color: 'from-pink-500 to-rose-500',
      bgColor: 'bg-pink-500/10'
    }
  ];

  // Parse daypart content into sub-sections
  const parseDaypartSubsections = (content) => {
    if (!content) return [];
    
    // Remove the header section (everything before the first ═══)
    const headerMatch = content.match(/^[\s\S]*?(?=═{3,})/);
    const contentWithoutHeader = headerMatch ? content.slice(headerMatch[0].length) : content;
    
    // Pattern to match section blocks
    // Each section starts with ═══, then title line, then ═══, then content until next ═══
    const sectionPattern = /═{3,}\s*\n([ΑΒΓΔΕΣΤΖΗΘΙΚΛΜ]\.[^\n]+)\s*\n(?:🎯[^\n]*\n)?═{3,}\s*\n([\s\S]*?)(?=═{3,}|$)/g;
    
    const subsections = [];
    let match;
    
    while ((match = sectionPattern.exec(contentWithoutHeader)) !== null) {
      const title = match[1].trim();
      const sectionContent = match[2].trim();
      
      if (title || sectionContent) {
        subsections.push({
          title: title,
          content: sectionContent
        });
      }
    }
    
    // Fallback: if regex didn't work, try simpler approach
    if (subsections.length === 0) {
      // Split by multiple equals signs
      const parts = content.split(/═{10,}/);
      let currentTitle = '';
      let currentContent = [];
      
      parts.forEach((part, idx) => {
        const trimmed = part.trim();
        if (!trimmed) return;
        
        // Check if this part contains a section title
        const titleMatch = trimmed.match(/^([ΑΒΓΔΕΣΤΖΗΘΙΚΛΜ]\.\s*[^\n]+)/m);
        if (titleMatch) {
          // Save previous section if exists
          if (currentTitle && currentContent.length > 0) {
            subsections.push({
              title: currentTitle,
              content: currentContent.join('\n').trim()
            });
          }
          
          currentTitle = titleMatch[1].trim();
          // Get content after the title (skip angle indicator line)
          const afterTitle = trimmed.slice(titleMatch.index + titleMatch[0].length)
            .replace(/🎯 ΣΗΜΕΡΙΝΗ ΓΩΝΙΑ:[^\n]*/g, '')
            .trim();
          currentContent = afterTitle ? [afterTitle] : [];
        } else if (currentTitle) {
          // This is content for the current section
          currentContent.push(trimmed);
        }
      });
      
      // Don't forget the last section
      if (currentTitle && currentContent.length > 0) {
        subsections.push({
          title: currentTitle,
          content: currentContent.join('\n').trim()
        });
      }
    }
    
    return subsections.length > 0 ? subsections : [{ title: '', content: content }];
  };

  // Check if this is a daypart category
  const isDaypartCategory = (categoryId) => {
    return ['morning', 'daytime', 'afternoon', 'evening', 'weekend'].includes(categoryId);
  };

  // Ref for storing scroll position before category toggle
  const scrollPositionRef = useRef(0);
  const buttonPositionRef = useRef(null);

  // Toggle category expansion
  const toggleCategory = useCallback((categoryId, e) => {
    // Prevent any default scroll behavior
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    // Store the button's position relative to the viewport
    if (e?.currentTarget) {
      const rect = e.currentTarget.getBoundingClientRect();
      buttonPositionRef.current = rect.top;
      scrollPositionRef.current = window.scrollY;
    }
    
    setExpandedCategories(prev => {
      const wasExpanded = prev[categoryId];
      return {
        ...prev,
        [categoryId]: !wasExpanded
      };
    });
  }, []);

  // Effect to restore scroll position after category expansion
  useEffect(() => {
    if (buttonPositionRef.current !== null) {
      // Use setTimeout to allow DOM to update first
      const timer = setTimeout(() => {
        // Calculate where the button should be now
        const targetScroll = scrollPositionRef.current;
        if (Math.abs(window.scrollY - targetScroll) > 50) {
          window.scrollTo({ top: targetScroll, behavior: 'instant' });
        }
        buttonPositionRef.current = null;
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [expandedCategories]);

  // Check if section has valid content
  const isValidSection = (sectionKey) => {
    // Check both 'sections' and 'content' paths for compatibility
    const section = content?.sections?.[sectionKey] || content?.content?.[sectionKey];
    if (!section) return false;
    if (section.skip) return false;
    if (!section.content || section.content.length < 50) return false;
    
    const hasPlaceholder = PLACEHOLDER_INDICATORS.some(indicator => 
      section.content && section.content.includes(indicator)
    );
    if (hasPlaceholder) return false;
    
    return true;
  };

  // Get valid sections for a category
  const getValidSections = (categorySections) => {
    return categorySections.filter(sectionKey => isValidSection(sectionKey));
  };

  // SEO - Multi-language
  const seoData = {
    el: {
      title: 'Περιεχόμενο On Air | Έτοιμο Ραδιοφωνικό Περιεχόμενο | Radio Solutions',
      description: 'Φρέσκο & μοναδικό περιεχόμενο για ραδιοφωνικές εκπομπές. 9 κατηγορίες: Πρωινό, Daytime, Απόγευμα, Βράδυ, Σαββατοκύριακο, Ζώδια, News, Entertainment, Charts. Ενημέρωση 24/7.',
      keywords: 'ραδιοφωνικό περιεχόμενο, ζώδια ημέρας, radio content, ραδιόφωνο, εκπομπές, morning show, drive time'
    },
    en: {
      title: 'On Air Content | Ready-to-Use Radio Content | Radio Solutions',
      description: 'Fresh & unique content for radio broadcasts. 9 categories: Morning, Daytime, Afternoon, Evening, Weekend, Zodiac, News, Entertainment, Charts. Updated 24/7.',
      keywords: 'radio content, daily horoscopes, broadcast content, radio programming, on air content, morning show, drive time'
    },
    de: {
      title: 'On Air Inhalte | Fertige Radio-Inhalte | Radio Solutions',
      description: 'Frische & einzigartige Inhalte für Radiosendungen. 9 Kategorien: Morgen, Mittag, Nachmittag, Abend, Wochenende, Horoskope, News, Entertainment, Charts. 24/7 aktualisiert.',
      keywords: 'Radio-Inhalte, tägliche Horoskope, Sendeinhalte, Radioprogrammierung, Morning Show'
    },
    it: {
      title: 'Contenuti On Air | Contenuti Radio Pronti | Radio Solutions',
      description: 'Contenuti freschi e unici per trasmissioni radiofoniche. 9 categorie: Mattina, Giorno, Pomeriggio, Sera, Weekend, Oroscopi, News, Entertainment, Charts. Aggiornato 24/7.',
      keywords: 'contenuti radio, oroscopi giornalieri, contenuti trasmissione, programmazione radio'
    },
    es: {
      title: 'Contenido On Air | Contenido de Radio Listo | Radio Solutions',
      description: 'Contenido fresco y único para transmisiones de radio. 9 categorías: Mañana, Día, Tarde, Noche, Fin de semana, Horóscopos, News, Entertainment, Charts. Actualizado 24/7.',
      keywords: 'contenido de radio, horóscopos diarios, contenido de transmisión, programación de radio'
    }
  };

  const currentSeo = seoData[language] || seoData.en;

  // Refresh content
  const refreshContent = async () => {
    setLoading(true);
    try {
      const token = storage.getItem('token');
      const headers = {
        'Cache-Control': 'no-cache',
        'Authorization': token ? `Bearer ${token}` : ''
      };
      const response = await axios.get(`${API}/api/content/latest?_t=${Date.now()}`, { headers });
      setContent(response.data);
    } catch (e) {
      console.error('Error refreshing content:', e);
    } finally {
      setLoading(false);
    }
  };

  // Download PDF with password protection
  const downloadPdf = async () => {
    if (!content?.date) {
      toast.error(language === 'el' ? 'Δεν υπάρχει περιεχόμενο για λήψη' : 'No content available for download');
      return;
    }
    
    setDownloadingPdf(true);
    try {
      const token = storage.getItem('token');
      if (!token) {
        toast.error(language === 'el' ? 'Παρακαλώ συνδεθείτε ξανά' : 'Please login again');
        return;
      }
      
      const username = currentUser?.username || currentUser?.email?.split('@')[0] || 'radio';
      
      const response = await axios.post(
        `${API}/api/export/download/onair-pdf`,
        {
          date: content.date,
          username: username
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          responseType: 'blob'
        }
      );
      
      // Check if response is actually an error (JSON instead of PDF)
      const contentType = response.headers['content-type'];
      if (contentType && contentType.includes('application/json')) {
        // Read error from blob
        const text = await response.data.text();
        const error = JSON.parse(text);
        throw new Error(error.detail || 'Unknown error');
      }
      
      // Create download link
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `OnAir_Content_${content.date}_${username}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success(
        language === 'el' 
          ? '✅ Το PDF κατέβηκε επιτυχώς!' 
          : '✅ PDF downloaded successfully!'
      );
    } catch (e) {
      console.error('Error downloading PDF:', e);
      
      // Try to extract error message from blob response
      let errorMessage = language === 'el' ? 'Σφάλμα κατά τη λήψη του PDF' : 'Error downloading PDF';
      
      if (e.response?.data instanceof Blob) {
        try {
          const text = await e.response.data.text();
          const errorData = JSON.parse(text);
          errorMessage = errorData.detail || errorMessage;
        } catch (parseError) {
          // Keep default message
        }
      } else if (e.message) {
        errorMessage = e.message;
      }
      
      toast.error(errorMessage);
    } finally {
      setDownloadingPdf(false);
    }
  };

  // Format date based on language
  const formatDate = (dateStr) => {
    const locales = {
      el: 'el-GR',
      en: 'en-US',
      de: 'de-DE',
      it: 'it-IT',
      es: 'es-ES'
    };
    return new Date(dateStr).toLocaleDateString(locales[language] || 'en-US', { 
      weekday: 'long', 
      day: 'numeric', 
      month: 'long', 
      year: 'numeric' 
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#1A0730] via-[#2D0C4B] to-[#1A0730]">
      {/* SEO Meta Tags - Title inherited from index.html (Radio Solutions Apps) */}
      <Helmet>
        <meta name="description" content={currentSeo.description} />
        <meta name="keywords" content={currentSeo.keywords} />
        <meta property="og:description" content={currentSeo.description} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:description" content={currentSeo.description} />
        <link rel="canonical" href={`${window.location.origin}/on-air-content`} />
      </Helmet>

      {/* Unified Header */}
      <ToolPageHeader title={t('onAirContent')} />

      {/* Content Section */}
      <section className="relative py-8 lg:py-12 overflow-hidden">
        {/* Background decoration */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-40 -right-40 w-96 h-96 bg-gradient-to-br from-purple-500/20 to-pink-500/20 rounded-full blur-3xl" />
          <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-gradient-to-br from-blue-500/20 to-cyan-500/20 rounded-full blur-3xl" />
        </div>

        <div className="container mx-auto px-4 relative z-10">
          {/* Page Info */}
          <div className="max-w-4xl mx-auto text-center mb-8">
            <div className={`inline-flex items-center gap-2 px-4 py-2 bg-white/10 rounded-full border border-white/20 mb-4 transition-all duration-500 ${animationStep >= 1 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
              <Radio className="w-5 h-5 text-[#FF9F0A]" />
              <span className="text-sm font-medium text-white">{t('onAirContent')}</span>
            </div>

            <h1 className={`text-2xl lg:text-4xl font-bold text-white mb-3 transition-all duration-500 ${animationStep >= 1 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
              {t('onAirPageTitle')}
            </h1>

            {content?.date && (
              <div className={`flex items-center justify-center gap-2 text-purple-200 mb-2 transition-all duration-500 ${animationStep >= 2 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
                <Clock className="w-4 h-4" />
                <span className="text-sm">
                  {formatDate(content.date)}
                </span>
                <button 
                  onClick={refreshContent}
                  className="ml-2 p-1 hover:bg-white/10 rounded-full transition-colors"
                  title={t('refresh')}
                  aria-label={t('refresh')}
                >
                  <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                </button>
                {/* PDF Download Button */}
                <button 
                  onClick={downloadPdf}
                  disabled={downloadingPdf}
                  className="ml-1 p-1 hover:bg-white/10 rounded-full transition-colors disabled:opacity-50"
                  title={language === 'el' ? 'Λήψη PDF' : 'Download PDF'}
                  aria-label={language === 'el' ? 'Λήψη PDF' : 'Download PDF'}
                >
                  {downloadingPdf ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Download className="w-4 h-4" />
                  )}
                </button>
              </div>
            )}

            {/* PDF Download Info */}
            {content?.date && (
              <div className={`mt-3 transition-all duration-500 ${animationStep >= 2 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
                <button
                  onClick={downloadPdf}
                  disabled={downloadingPdf}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[#FF9F0A] to-[#FF6B00] hover:from-[#FF6B00] hover:to-[#FF9F0A] text-white font-medium rounded-full transition-all duration-300 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {downloadingPdf ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <FileText className="w-4 h-4" />
                  )}
                  <span>{language === 'el' ? '📄 Λήψη PDF' : '📄 Download PDF'}</span>
                </button>
                <p className="text-xs text-purple-300 mt-1">
                  {language === 'el' 
                    ? 'Το PDF προστατεύεται με κωδικό'
                    : 'PDF is password protected'
                  }
                </p>
              </div>
            )}

            <p className={`text-sm text-purple-200 transition-all duration-500 ${animationStep >= 2 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
              {t('onAirPageSubtitle')}
            </p>
            
            {/* Translation Status Indicator */}
            {language !== 'el' && translationStatus && (
              <div className={`mt-2 transition-all duration-500 ${animationStep >= 2 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
                {translationStatus.languages?.[language]?.status === 'available' ? (
                  <span className="inline-flex items-center gap-1 text-xs text-green-400 bg-green-500/10 px-3 py-1 rounded-full border border-green-500/20">
                    🌍 {t('translatedContent') || 'Translated content available'}
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-xs text-yellow-400 bg-yellow-500/10 px-3 py-1 rounded-full border border-yellow-500/20">
                    ⏳ {t('originalContent') || 'Showing original content'}
                  </span>
                )}
              </div>
            )}
          </div>

          {/* Loading State */}
          {loading && !content && (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 text-[#FF9F0A] animate-spin" />
              <span className="ml-3 text-white">{t('loadingContent')}</span>
            </div>
          )}

          {/* Categories with Expandable Sections */}
          {content && (
            <div className="max-w-4xl mx-auto space-y-4">
              {categories.map((category, index) => {
                const Icon = category.icon;
                const isExpanded = expandedCategories[category.id];
                const validSections = getValidSections(category.sections);
                const delay = (index + 2) * 100;

                if (validSections.length === 0) return null;

                return (
                  <div 
                    key={category.id}
                    className={`rounded-2xl overflow-hidden transition-all duration-500 ${animationStep >= 3 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
                    style={{ transitionDelay: `${delay}ms` }}
                    data-testid={`category-${category.id}`}
                  >
                    {/* Category Header - Clickable */}
                    <button 
                      type="button"
                      onClick={(e) => toggleCategory(category.id, e)}
                      onMouseDown={(e) => e.preventDefault()} 
                      className={`
                        w-full p-5 flex items-center gap-4
                        bg-white/5 backdrop-blur-sm
                        border border-white/10
                        hover:border-white/30 hover:bg-white/10
                        transition-all duration-300
                        focus:outline-none
                        ${isExpanded ? 'rounded-t-2xl border-b-0' : 'rounded-2xl'}
                      `}
                      aria-expanded={isExpanded}
                      data-testid={`category-button-${category.id}`}
                      tabIndex={-1}
                    >
                      {/* Icon */}
                      <div className={`
                        flex-shrink-0 w-12 h-12 rounded-xl 
                        bg-gradient-to-br ${category.color} 
                        flex items-center justify-center
                        shadow-lg
                      `}>
                        <Icon className="w-6 h-6 text-white" />
                      </div>

                      {/* Content */}
                      <div className="flex-1 text-left">
                        <h2 className="text-lg font-bold text-white">
                          {category.title}
                        </h2>
                        <p className="text-sm text-purple-200">
                          {/* Hide section count for daypart categories since they contain sub-sections */}
                          {['morning', 'daytime', 'afternoon', 'evening', 'weekend'].includes(category.id) 
                            ? (language === 'el' ? 'Πατήστε για περιεχόμενο' : 'Tap to view content')
                            : `${validSections.length} ${t('sectionsAvailable')}`
                          }
                        </p>
                      </div>

                      {/* Expand Icon */}
                      <div className="text-purple-200">
                        {isExpanded ? (
                          <ChevronDown className="w-6 h-6" />
                        ) : (
                          <ChevronRight className="w-6 h-6" />
                        )}
                      </div>
                    </button>

                    {/* Expanded Sections */}
                    {isExpanded && (
                      <div className="bg-white/5 backdrop-blur-sm border border-white/10 border-t-0 rounded-b-2xl p-4 space-y-3">
                        {validSections.map(sectionKey => {
                          // Support both 'sections' and 'content' paths
                          const originalSection = content.sections?.[sectionKey] || content.content?.[sectionKey];
                          // Use translation if available for current language
                          const translatedSection = (language !== 'el' && translations?.[sectionKey]) || null;
                          const section = translatedSection || originalSection;
                          const sectionConfig = SECTIONS_CONFIG[sectionKey] || {};
                          
                          // Show translation indicator
                          const isTranslated = !!translatedSection;
                          
                          return (
                            <div 
                              key={sectionKey}
                              className="bg-[#1A0730]/60 rounded-xl p-4 hover:bg-[#1A0730]/80 transition-colors border border-white/5"
                              data-testid={`section-${sectionKey}`}
                            >
                              {/* Section Header */}
                              <div className="flex items-center justify-between mb-3">
                                <div className="flex items-center gap-2">
                                  <Badge className="bg-gradient-to-r from-[#2D0C4B] to-[#4A1D7A] text-white text-sm px-3 py-1 border-0">
                                    {sectionConfig.emoji} {sectionConfig.title || section.title || sectionKey}
                                  </Badge>
                                  {isTranslated && (
                                    <span className="text-xs text-green-400 flex items-center gap-1">
                                      🌍 {language.toUpperCase()}
                                    </span>
                                  )}
                                </div>
                                <div className="flex items-center gap-2">
                                  {section.generated_at && (
                                    <span className="text-xs text-purple-300">
                                      {new Date(section.generated_at).toLocaleString(language === 'el' ? 'el-GR' : 'en-US', {
                                        hour: '2-digit',
                                        minute: '2-digit'
                                      })}
                                    </span>
                                  )}
                                  {/* Admin indicator + AI editor buttons
                                      (admin OR users with categories_on_air feature) */}
                                  {(currentUser?.role === 'admin' || hasFeatureAccess('categories_on_air')) && (
                                    <>
                                      {currentUser?.role === 'admin' && (
                                        <span className="text-xs text-amber-400 flex items-center gap-1" title="Admin view">
                                          <Shield className="w-3 h-3" />
                                        </span>
                                      )}
                                      <button
                                        onClick={() => setAiEditor({
                                          open: true,
                                          mode: 'edit',
                                          sectionKey,
                                          sectionTitle: sectionConfig.title || section.title || sectionKey,
                                          currentText: section.content || '',
                                        })}
                                        className="text-xs px-2 py-1 rounded-md bg-purple-500/20 hover:bg-purple-500/40 text-purple-100 border border-purple-500/30 flex items-center gap-1 transition-colors"
                                        data-testid={`ai-edit-btn-${sectionKey}`}
                                        title="Επεξεργασία με AI"
                                      >
                                        <Wand2 className="w-3 h-3" />
                                        <span className="hidden sm:inline">Edit</span>
                                      </button>
                                      <button
                                        onClick={() => setAiEditor({
                                          open: true,
                                          mode: 'add',
                                          sectionKey,
                                          sectionTitle: sectionConfig.title || section.title || sectionKey,
                                          currentText: section.content || '',
                                        })}
                                        className="text-xs px-2 py-1 rounded-md bg-green-500/20 hover:bg-green-500/40 text-green-100 border border-green-500/30 flex items-center gap-1 transition-colors"
                                        data-testid={`ai-add-btn-${sectionKey}`}
                                        title="Προσθήκη νέου θέματος"
                                      >
                                        <Plus className="w-3 h-3" />
                                        <span className="hidden sm:inline">Add topic</span>
                                      </button>
                                    </>
                                  )}
                                </div>
                              </div>
                              
                              {/* Section Content */}
                              <div className="max-h-[500px] overflow-y-auto bg-[#0D0318]/60 rounded-lg p-4 border border-white/5">
                                {/* Show EITHER items OR content, not both */}
                                {section.items_with_images && section.items_with_images.length > 0 ? (
                                  <div className="space-y-3">
                                    {section.items_with_images.map((item, idx) => (
                                      item.is_header ? (
                                        <div key={idx} className="mt-4 mb-2">
                                          <h3 className="text-md font-bold text-purple-300 border-b border-purple-500/30 pb-1">
                                            {cleanContent(item.text)}
                                          </h3>
                                        </div>
                                      ) : (
                                        <div key={idx} className="flex items-start gap-3 p-2 bg-white/5 rounded border border-white/5">
                                          {/* YouTube thumbnails enabled */}
                                          {sectionKey === 'youtube_trending_greece' && item.image_url && (
                                            <a 
                                              href={item.link} 
                                              target="_blank" 
                                              rel="noopener noreferrer"
                                              className="flex-shrink-0 rounded overflow-hidden hover:ring-2 hover:ring-purple-500 transition-all"
                                            >
                                              <SafeImage 
                                                src={item.image_url} 
                                                alt={item.title || item.text} 
                                                className="w-32 h-18 object-cover"
                                              />
                                            </a>
                                          )}
                                          <div className="flex-1">
                                            {item.name && (
                                              <span className="text-xs font-bold text-purple-400 mr-2">
                                                {item.name}
                                              </span>
                                            )}
                                            {item.link ? (
                                              <a 
                                                href={item.link} 
                                                target="_blank" 
                                                rel="noopener noreferrer"
                                                className="text-sm text-gray-100 hover:text-purple-300 transition-colors"
                                              >
                                                {cleanContent(item.text || item.title)}
                                              </a>
                                            ) : (
                                              <span className="text-sm text-gray-100">
                                                {cleanContent(item.text)}
                                              </span>
                                            )}
                                          </div>
                                        </div>
                                      )
                                    ))}
                                  </div>
                                ) : isDaypartCategory(category.id) && section.content ? (
                                  /* Special rendering for daypart categories - parse into sub-sections */
                                  <div className="space-y-4">
                                    {parseDaypartSubsections(section.content).map((subsec, idx) => (
                                      <div key={idx} className="bg-white/5 rounded-lg p-3 border border-white/10">
                                        {subsec.title && (
                                          <h4 className="text-md font-bold text-purple-300 mb-2 border-b border-purple-500/30 pb-1">
                                            {cleanContent(subsec.title)}
                                          </h4>
                                        )}
                                        {subsec.content && (
                                          <div 
                                            className="text-sm text-gray-100 leading-relaxed"
                                            dangerouslySetInnerHTML={renderMarkdown(cleanContent(subsec.content))}
                                          />
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                ) : (
                                  section.content && section.content.trim().length > 0 && (
                                    <div 
                                      className="text-sm text-gray-100 leading-relaxed"
                                      dangerouslySetInnerHTML={renderMarkdown(cleanContent(section.content))}
                                    />
                                  )
                                )}
                              </div>
                              
                              {/* Admin Date Navigator - Only for admins */}
                              {currentUser?.role === 'admin' && (
                                <DateNavigator
                                  currentDate={content.date}
                                  isAdmin={currentUser?.role === 'admin'}
                                  sectionKey={sectionKey}
                                  backendUrl={getBackendUrl()}
                                  token={storage.getItem('token')}
                                />
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Quick Stats - 2x2 Grid */}
          {content && (
            <div className={`mt-8 grid grid-cols-2 gap-4 max-w-md mx-auto transition-all duration-500 ${animationStep >= 4 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
              <div className="text-center p-4 bg-white/5 rounded-xl border border-white/10">
                <div className="text-base font-bold text-[#FF9F0A]">
                  {language === 'el' ? '🔄 Φρέσκο & Μοναδικό' : '🔄 Fresh & Unique'}
                </div>
                <div className="text-xs text-purple-200">{language === 'el' ? 'Περιεχόμενο' : 'Content'}</div>
              </div>
              <div className="text-center p-4 bg-white/5 rounded-xl border border-white/10">
                <div className="text-2xl font-bold text-[#FF9F0A]">9</div>
                <div className="text-xs text-purple-200">{language === 'el' ? 'Κατηγορίες' : 'Categories'}</div>
              </div>
              <div className="text-center p-4 bg-white/5 rounded-xl border border-white/10">
                <div className="text-2xl font-bold text-[#FF9F0A]">24/7</div>
                <div className="text-xs text-purple-200">{t('updates')}</div>
              </div>
              <div className="text-center p-4 bg-white/5 rounded-xl border border-white/10">
                <div className="text-2xl font-bold text-[#FF9F0A]">100%</div>
                <div className="text-xs text-purple-200">{t('ready')}</div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Radio Apps Clock Builder section — gated behind categories_on_air */}
      {(hasFullAccess || hasFeatureAccess('categories_on_air')) && (
        <section className="max-w-4xl mx-auto px-4 py-8" data-testid="radio-clock-section">
          <div className="bg-gradient-to-br from-[#1B2A4A] via-[#2D3E60] to-[#1B2A4A] rounded-2xl p-6 md:p-8 border border-purple-800/40 shadow-xl">
            <div className="flex flex-col md:flex-row items-start md:items-center gap-4 md:gap-6">
              <div className="w-16 h-16 rounded-2xl bg-[#FF9F0A]/20 flex items-center justify-center flex-shrink-0">
                <ClockIcon className="text-[#FF9F0A]" size={32} />
              </div>
              <div className="flex-1">
                <h3 className="text-2xl font-bold text-white mb-2">
                  🕒 Radio Apps Clock Builder
                </h3>
                <p className="text-purple-200 text-sm leading-relaxed">
                  {language === 'el'
                    ? 'Στήσε το ραδιοφωνικό σου πρόγραμμα ώρα-ώρα, για όλη την εβδομάδα. Επιλέγεις τι μπαίνει στον αέρα — τραγούδι, ομιλία, δελτίο, διαφημιστικό break — και το εργαλείο ζωγραφίζει άμεσα το clock της κάθε ώρας. Αποθηκεύεις όσα προγράμματα θέλεις στον λογαριασμό σου και τα έχεις μαζί σου.'
                    : 'Design your radio programming hour by hour, for the whole week. Pick what airs — songs, talk, news, breaks — and see the clock draw itself. Save unlimited programs to your account.'}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setClockBuilderOpen(true)}
                data-testid="open-radio-clock-builder-btn"
                className="bg-[#FF9F0A] hover:bg-[#FFB84D] text-[#2D0C4B] font-bold px-5 py-3 rounded-xl text-sm shadow-lg transition-all hover:scale-[1.03] flex items-center gap-2 flex-shrink-0"
              >
                <ClockIcon size={18} />
                {language === 'el' ? 'Άνοιξε το Builder' : 'Open Builder'}
              </button>
            </div>
          </div>
        </section>
      )}

      {/* Footer is rendered by App.js wrapper */}
      <RadioClockBuilder
        open={clockBuilderOpen}
        onClose={() => setClockBuilderOpen(false)}
        user={currentUser}
      />
      <AIContentEditorModal
        open={aiEditor.open}
        onClose={() => setAiEditor((s) => ({ ...s, open: false }))}
        mode={aiEditor.mode}
        sectionKey={aiEditor.sectionKey}
        sectionTitle={aiEditor.sectionTitle}
        currentText={aiEditor.currentText}
        date={content?.date}
        onSaved={() => refreshContent()}
      />
    </div>
  );
};

export default OnAirContentPage;
